/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

import {
  Component,
  ComponentRef,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  Type,
  ViewChild
} from '@angular/core';
import {Sihwlog} from 'sihw-ngx';
import {AngularDraggableDirective, AngularResizableDirective} from 'angular2-draggable';
import {WindowedComponent} from '../windowedcomponent.class';
import {SihwWindowmanager, WindowCreateOptions} from '../windowmanager/windowmanager';


/**
 * <window></window>
 * Een window met daarin een component (van een subclass van WindowedComponent)
 * Is gekoppeld aan de windowmanager (met publieke services) en aan een windowedComponent (zijn inhoud)
 */
@Component({

  selector: 'window',
  templateUrl: './window.component.html',
  styleUrls: ['./window.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class WindowComponent implements OnInit {
  public windowedInputs: any = {}; //de inputs - die worden nog uitgebreid

  private windowed: WindowedComponent = null; //welk component tonen wij? Gezet via event

  //tbv slepen e.d.
  @ViewChild('draggableWindow', {static: true}) private draggableWindow: AngularDraggableDirective;
  @ViewChild('resizableWindow', {static: true}) private resizableWindow: AngularResizableDirective;
  @ViewChild('asElement', {static: true}) private asElement: ElementRef;

  @Input() manager: SihwWindowmanager; //onze manager
  @Input() handle: string; //onze id in de windowmanager
  @Input() public component: Type<WindowedComponent>; //wordt doorgezet naar ndcDynamicComponent
  //inputs is er ook, maar dat is een setter: zie verder

  @Input() public position; //wordt eventueel gezet in init
  private log: Sihwlog;

  public maximized: boolean = false;
  public blockCount: number = 0; //aantal optiks richting een blockscherm, zie createWindow
  private restoredPosition: { x: number, y: number } = null; //voor maximaliseren
  private restoredSize: { w: number, h: number } = null; //voor maximaliseren
  sizeClasses = {
    sm: true,
    md: true,
    lg: false,
    xl: false,
  }; //vier classes windowsize-sm etc, die we gebruiken voor het aanpassen van de bootstrap col-md-3 etc classes als de class windowgrid actief is. Zie windowgrid.scss

  private focussedChild: HTMLElement; //wordt gezet bij verliezen van focus, en poging het weer goed te doen bij terugkrijgen van focus
  constructor(private renderer: Renderer2) {
    this.log = new Sihwlog('debug', 'Window');
  }


  //inputs is een setter
  @Input() set inputs(d: any) {
    this.windowedInputs = Object.assign({},
      d || {},
      {
        _window: this //wij
      }
    );
  }

  ngOnInit() {
    //positie?
    if (!this.position) {
      //we zetten een standaard, beetje suf
      let numwindows = this.manager.numWindows - 1; //deze telt al mee+
      this.position = {
        x: 10 + Math.max(0, numwindows * 20),
        y: 10 + Math.max(0, numwindows * 20)
      };
    }
    //meld de manager dat we er zijn
    this.manager.setWindow(this.handle, this);

    //bewaar het element met focus
    this.asElement.nativeElement.addEventListener('focus', (event: Event) => {
        this.focussedChild = <HTMLElement> event.target;
      },
      {capture: true, passive: true});
  }

  /**
   * De classnaam van onze windowedcomponent. Let op: de builder hernoemt classes, dus in een bundel
   * staat hier `_Planbordscherm` en in een productiebuild een geminificeerde naam. Gebruik dit dus
   * niet als herkenningspunt van buitenaf; daar is windowedname voor
   */
  get windowedclass(): string {
    return (this.windowed && this.windowed.constructor.name) || '';
  }

  /**
   * De geregistreerde naam van onze windowedcomponent: de letterlijke string uit
   * Planbordscherm.register('Planbordscherm'). Die overleeft elke build en staat als attribuut
   * `windowed` op het window, zodat tests een scherm kunnen aanwijzen
   */
  get windowedname(): string {
    return (this.windowed && (this.windowed.constructor as typeof WindowedComponent).registeredName) || this.windowedclass;
  }

  get ref(): ElementRef {
    return this.asElement;
  }

  get title(): string {
    return (this.windowed && this.windowed.title) || ''; //door
  }

  get icon(): string {
    return (this.windowed && this.windowed.icon) || ''; //door
  }

  get fixedSize(): boolean {
    return (this.windowed && this.windowed.fixedSize);
  }

  /**
   * true als we een close-button tonen. Onze windows kan dat op false zetten
   */
  get closeButton(): boolean {
    return this.windowed ? this.windowed.closeButton : true;
  }

  /**
   * Het id van onze body-div. Uniek per window, want de handle van de windowmanager is dat ook.
   */
  get bodyId(): string {
    return `windowbody_${this.handle}`;
  }

  /**
   * De css-selector van de container waarin losstaande dropdowns (ng-select appendTo) geplaatst
   * kunnen worden. Dat is onze body: die valt buiten de scrollende contentholder, maar zit wél
   * binnen dit window en schuift dus mee als het window verplaatst of geresized wordt.
   */
  get dropdownContainer(): string {
    return `#${this.bodyId}`;
  }

  ///closing hook
  public closing() {
    //meldt het het component
    if (this.windowed) {
      this.windowed.closing();
    }
  }

  //focus hooks
  /**
   * Bericht dat we focus gaan verliezen
   */
  public losingFocus() {
    //focussedChild wordt via eventlistener gezet
    //we detachen: geen changes meer
    // this.cdf.detach();
    if (this.windowed) {
      this.windowed.losingFocus();
    }
  }

  /**
   * Bericht dat we focus gaan krijgen. Wordt wellicht niet goed afgehandeld bij een nieuw window, dus let op defaults
   */
  public gettingFocus() {
    //dus weer changedetection
    if (this.windowed) {
      this.windowed.gettingFocus();
    }
    //als we een focuschild hadden en er is nu geen focus binnen ons window: focussen
    let currentfocus = this.asElement.nativeElement.querySelector('*:focus');
    //als er een currentfocus is -die anders is dan de vorige- dan is er
    //door de user in een ander control geklikt
    //als de currentfocus hetzelfde is als de vorige, dan moeten we de
    //focus er wel opnieuw opzetten, omdat dit een refocus is na eventueel
    //een klik in een ander deel van het browserscherm. De actieve
    //focus op inputs is dan weg
    if (this.focussedChild && ((!currentfocus) || (currentfocus === this.focussedChild))) {

      setTimeout(_ => {
        try {
          this.focussedChild.focus();
        } catch (e) {
          this.log.debug(e); //mislukt. soit!
        }
      });
    }
  }

  /////////////////////// public service


  public get actief(): boolean {
    //is dit het actieve window? Dat weet de manager
    return this.manager && this.manager.actief(this);
  }


  /**
   * Activeer het window
   */
  public naarVoren() {
    //dat regelt de manager
    this.manager.naarVoren(this);
  }


  public toggleMaximize(): void {
    this.maximize(!this.maximized);
  }

  /**
   * Maximaliseer of restore
   * @param doeMaximize
   */
  public maximize(doeMaximize: boolean = true) {
    this.maximized = doeMaximize;

    if (this.maximized) {
      this.restoredPosition = this.draggableWindow.getCurrentOffset(); //onthouden
      let windowRect = this.asElement.nativeElement.getBoundingClientRect();
      this.restoredSize = {w: windowRect.width, h: windowRect.height};
      this.position = {x: 0, y: 0}; //haal de transform weg
      //en de size:
      this.renderer.removeStyle(this.asElement.nativeElement, 'height');
      this.renderer.removeStyle(this.asElement.nativeElement, 'width');
    } else {
      if (this.restoredPosition) {
        this.position = this.restoredPosition;
      }
      if (this.restoredSize) {
        //zelf regelen
        this.renderer.setStyle(this.asElement.nativeElement, 'width', `${this.restoredSize.w}px`);
        this.renderer.setStyle(this.asElement.nativeElement, 'height', `${this.restoredSize.h}px`);
      }
    }
    this.draggableWindow.resetPosition();  //weg met de transform
    //en de sizeclasses, na hertekenen
    setTimeout(_ => {
      this.setSizeclasses();
    });
  }

  /**
   * Sluit dit window via de sluitknop of op verzoek. Kan worden onderbroken via de canClose hook
   * tenzij forceer true is
   */
  public async close(resultaat: any = null, forceer: boolean = false): Promise<void> {
    //onze manager gaat erover, onze windowed mag het tegenhouden
    if (forceer || (await this.windowed.canClose())) {
      this.manager.close(this, resultaat);
    }
  }

  /**
   * Open een ander window
   * @param component
   * @param data
   * @param options
   * @param block Blokkeer het aanroepende window tot het andere window weg is
   */
  public async createWindow(component: Type<WindowedComponent> | string, data: any = {}, options: WindowCreateOptions = {}, block = false): Promise<any> {

    if (block) {
      this.blockCount++;
    }
    let result = await this.manager.create(component, data, options);
    if (block) {
      this.blockCount--;
    }
    return result;
  }


  ///////////////////////////////////////////// INTERN ////////////////////////////////////
  /**
   * Shortcut naar de boundingcontainer van de manager
   */
  get container(): HTMLElement {
    return (this.manager && this.manager.container && this.manager.container.nativeElement) || null;
  }

  /**
   * Check of window uit beeld valt, en fix dat
   * Dit gebeurt na slepen en door de manager bij resize
   */
  public fixPosition() {

    if (!this.container) {
      return; //niet te doen
    }

    let parentRect = this.container.getBoundingClientRect();
    let windowRect = this.asElement.nativeElement.getBoundingClientRect();
    this.position = this.draggableWindow.getCurrentOffset();

    //te hoog
    if (windowRect.top < parentRect.top) {
      this.position.y += (parentRect.top - windowRect.top);
    }

    //te laag?
    if (windowRect.top > parentRect.bottom - 40) {
      this.position.y -= (windowRect.top - (parentRect.bottom - 40));
    }

    //te links?
    if (windowRect.right < (parentRect.left + 40)) {
      this.position.x += (parentRect.left + 40) - windowRect.right;
    }

    //te rechts?
    if (windowRect.left > (parentRect.right - 40)) {
      this.position.x -= windowRect.left - (parentRect.right - 40);
    }
    this.draggableWindow.resetPosition();//this.position opnieuw gebruiken
    //en de sizeclasses, na hertekenen, want het is vaak een browser (en bij maximized verandert dit window dan ook)
    setTimeout(_ => {
      this.setSizeclasses();
    });
  }


  //////////////////////// PRIVATE EVENTS ///////////////////
  ///initialiseer het window
  contentCreated(windowedRef: ComponentRef<WindowedComponent>) {
    //even async, in de volgende ngcheck, anders krijgen van changedaftercheck-errors
    setTimeout(() => {
      this.windowed = windowedRef.instance;
      this.log.debug(this.windowed);
      if (this.windowed.fixedHeight) {
        const h = this.windowed.fixedHeight;
        this.renderer.setStyle(this.asElement.nativeElement, 'height', Number.isInteger(<number> h) ? `${h}px` : h);
      }
      if (this.windowed.fixedWidth) {
        const w = this.windowed.fixedWidth;
        this.renderer.setStyle(this.asElement.nativeElement, 'width', Number.isInteger(<number> w) ? `${w}px` : w);
      }
      else
      {
        //misschien opgeslagen?
        const w = localStorage[`${this.windowedname}_b`];
        if (w === 'm')
        {
          this.log.debug(`Window herstellen in maximized`)
          this.maximize();
        }
        else if (w && Number.isInteger(parseInt(w,10)) && parseInt(w,10) > 100)
        {
          this.log.debug(`Window herstellen naar breedte ${w}`);
          this.renderer.setStyle(this.asElement.nativeElement, 'width',`${w}px`);
        }
      }

      //na hertekenen eventueel maximaliseren, en de sizeclasses
      setTimeout(_ => {
        if (this.windowed.openMaximized) {
          this.maximize(true);
        } else {
          //dan gewoon sizeclasses
          this.setSizeclasses();
        }
      });
    });

  }

  ///////////////////////// sizeclasses ///////////////////////
  /**
   * Aanroepen na resize: zet de windowsize classes goed op basis van de huidige breedte. Zodat we wcol-sm-2 etc kunnen doen (zie windowgrid.scss stylesheet)

   */
  setSizeclasses() {
    let breedte = this.asElement.nativeElement.clientWidth;
    //opslaan in localstorage voor de volgende keer op deze computer
    if (this.windowed)
    {
      localStorage[`${this.windowedname}_b`] = this.maximized ? 'm'  : breedte;
    }
    //welke grootteklasses, zelfde breakpoints als bootstrap
    //windowsize-xs is er altijd, gewoon hard in de div class
    this.sizeClasses.sm = (breedte >= 576);
    this.sizeClasses.md = (breedte >= 768);
    this.sizeClasses.lg = (breedte >= 992);
    this.sizeClasses.xl = (breedte >= 1200);
  }
}
